/**
 * "Views": the queues a person has saved, and the one they start on (087).
 *
 * A view is everything that decides what the list shows and how: the tab,
 * list or board, the sort, the queue and type filters, whose tickets, a tag,
 * and row height. Saving one keeps it; choosing it puts all of that back.
 * The one marked "Start here" is what the queue opens on when it is opened
 * plainly, from the menu or after signing in -- a link that already says
 * what to show (from a report, say) still wins.
 *
 * The menu acts on the view that is showing. Change anything and it is no
 * longer that view, so the menu offers to save the change as a new one or
 * to update the one it came from.
 */
import { useState } from 'react';
import { Bookmark, Check, Pencil, Plus, RefreshCw, Star, Trash2 } from 'lucide-react';
import { MAX_SAVED_VIEWS, type QueuePrefs, type SavedView } from '@esa/shared';
import {
  Button,
  Dialog,
  DialogContent,
  Field,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Switch,
} from '../ui/index.js';

/** What the queue is showing now, in a saved view's shape (no id or name). */
export type ViewState = Omit<SavedView, 'id' | 'name'>;

/** The same view: every field equal, a missing field meaning the default. */
export function sameView(a: ViewState, b: ViewState): boolean {
  const keys: (keyof ViewState)[] = ['view', 'mode', 'sort', 'queueKey', 'requestType', 'assigneeOid', 'tag', 'density'];
  const norm = (s: ViewState, k: keyof ViewState) => {
    const v = s[k] ?? '';
    if (k === 'mode') return v || 'list';
    if (k === 'sort') return v || 'updated_desc';
    if (k === 'density') return v || 'normal';
    return v;
  };
  return keys.every((k) => norm(a, k) === norm(b, k));
}

const newId = () => Math.random().toString(36).slice(2, 10);

export function SavedViews({
  prefs,
  current,
  onApply,
  onSave,
  saving,
  cameFrom,
}: {
  prefs: QueuePrefs;
  current: ViewState;
  /** The saved view last chosen, which the current one may be a change of. */
  cameFrom: string | null;
  onApply: (v: SavedView) => void;
  onSave: (next: QueuePrefs) => void;
  saving: boolean;
}) {
  const [naming, setNaming] = useState<{ id: string | null; name: string; start: boolean } | null>(null);
  const showing = prefs.views.find((v) => sameView(v, current)) ?? null;
  const full = prefs.views.length >= MAX_SAVED_VIEWS;
  const changedFrom = showing ? null : (prefs.views.find((v) => v.id === cameFrom) ?? null);

  const save = () => {
    if (!naming || !naming.name.trim()) return;
    const name = naming.name.trim();
    let views: SavedView[];
    let id = naming.id;
    if (id) {
      views = prefs.views.map((v) => (v.id === id ? { ...v, name } : v));
    } else {
      id = newId();
      views = [...prefs.views, { ...current, id, name }];
    }
    const defaultViewId = naming.start ? id : prefs.defaultViewId === id ? null : prefs.defaultViewId;
    onSave({ ...prefs, views, defaultViewId });
    setNaming(null);
  };

  return (
    <>
      <Menu>
        <MenuTrigger asChild>
          <Button variant="secondary" size="sm" leading={<Bookmark className="h-3.5 w-3.5" />} aria-label="Saved views">
            <span className="max-w-[9rem] truncate">{showing ? showing.name : 'Views'}</span>
          </Button>
        </MenuTrigger>
        <MenuContent className="w-64">
          <MenuLabel>Your views</MenuLabel>
          {prefs.views.length === 0 && (
            <p className="px-2 pb-2 text-2xs text-ink-tertiary">
              Set up the tab, filters and sort you want, then save it here to come back to in one click.
            </p>
          )}
          {prefs.views.map((v) => (
            <MenuItem
              key={v.id}
              icon={v.id === showing?.id ? <Check /> : v.id === prefs.defaultViewId ? <Star /> : <span className="inline-block h-3.5 w-3.5" />}
              onSelect={() => onApply(v)}
            >
              {v.name}
              {v.id === prefs.defaultViewId && <span className="ml-1.5 text-2xs text-ink-tertiary">start here</span>}
            </MenuItem>
          ))}
          <MenuSeparator />
          <MenuItem
            icon={<Plus />}
            disabled={full || Boolean(showing)}
            onSelect={() => setNaming({ id: null, name: '', start: prefs.views.length === 0 })}
          >
            {full ? `Up to ${MAX_SAVED_VIEWS} views` : showing ? 'This view is saved' : 'Save as a new view…'}
          </MenuItem>
          {changedFrom && (
            <MenuItem
              icon={<RefreshCw />}
              onSelect={() =>
                onSave({
                  ...prefs,
                  views: prefs.views.map((v) => (v.id === changedFrom.id ? { ...current, id: v.id, name: v.name } : v)),
                })
              }
            >
              Update “{changedFrom.name}” to this
            </MenuItem>
          )}
          {showing && (
            <>
              <MenuItem
                icon={<Star />}
                onSelect={() =>
                  onSave({ ...prefs, defaultViewId: prefs.defaultViewId === showing.id ? null : showing.id })
                }
              >
                {prefs.defaultViewId === showing.id ? 'Stop starting here' : 'Start here when I open the queue'}
              </MenuItem>
              <MenuItem
                icon={<Pencil />}
                onSelect={() => setNaming({ id: showing.id, name: showing.name, start: prefs.defaultViewId === showing.id })}
              >
                Rename…
              </MenuItem>
              <MenuItem
                icon={<Trash2 />}
                danger
                onSelect={() =>
                  onSave({
                    ...prefs,
                    views: prefs.views.filter((v) => v.id !== showing.id),
                    defaultViewId: prefs.defaultViewId === showing.id ? null : prefs.defaultViewId,
                  })
                }
              >
                Delete “{showing.name}”
              </MenuItem>
            </>
          )}
        </MenuContent>
      </Menu>

      <Dialog open={naming !== null} onOpenChange={(o) => !o && setNaming(null)}>
        <DialogContent
          title={naming?.id ? 'Rename view' : 'Save this view'}
          description={
            naming?.id
              ? undefined
              : 'Keeps the tab, filters, sort and layout you have now. Only you see your views.'
          }
        >
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <Field label="Name" required>
              <Input
                autoFocus
                value={naming?.name ?? ''}
                maxLength={60}
                placeholder="My overdue, oldest first"
                onChange={(e) => setNaming((n) => (n ? { ...n, name: e.target.value } : n))}
              />
            </Field>
            <Switch
              checked={naming?.start ?? false}
              onCheckedChange={(start) => setNaming((n) => (n ? { ...n, start } : n))}
              label="Start here when I open the queue"
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setNaming(null)}>
                Cancel
              </Button>
              <Button type="submit" loading={saving} disabled={!naming?.name.trim()}>
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
